/**
 * 2026-10-29 (owner) · "Copy step" in the Add step picker: the steps of every operation of this process,
 * operation-wise, and one of them copied to where the step is being added ("step add karte time copy step
 * ka de sakte hai kya, modal me usi process ke operations ke step ki list aayegi copy karne ke liye,
 * operation wise"). The server copies the step with everything it carries (services/recipe/step_copy.py).
 * A step whose type this operation does not take is shown, but cannot be picked.
 */
import React, { useMemo, useState } from "react";
import { Button, Collapse, Empty, Input, Modal, Tag, Tooltip } from "antd";
import { Copy } from "lucide-react";

const KIND_TEXT = { LOOP: "Repeat", DECISION: "Decision" };

/** The rows of an operation a person may copy: every step, repeat and decision, nested ones indented. */
function rowsOf(nodes, depth = 0, out = []) {
  (nodes || []).forEach((n) => {
    if (n.kind === "LANE") { rowsOf(n.children, depth, out); return; }
    out.push({ node: n, depth });
    if (n.kind !== "DECISION") rowsOf(n.children, depth + 1, out);
    else (n.children || []).forEach((lane) => rowsOf(lane.children, depth + 1, out));
  });
  return out;
}

export default function CopyStepDialog({ open, onClose, operations, current, allowed, typeLabel, onCopy }) {
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(null);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (operations || []).map((o) => ({
      op: o,
      rows: rowsOf(o.children).filter(({ node }) => !q
        || `${node.code} ${node.title || ""} ${typeLabel(node.step_type) || ""}`.toLowerCase().includes(q)),
    })).filter((g) => !q || g.rows.length);
  }, [operations, query, typeLabel]);

  const why = (n) => {
    const t = n.kind === "STEP" ? n.step_type : n.kind;
    if (allowed && t && !allowed.has(t)) return `${typeLabel(n.step_type) || KIND_TEXT[n.kind] || t} is not a step type of this operation`;
    return null;
  };

  const copy = async (n) => {
    setBusy(n.code);
    try {
      const ok = await onCopy(n.code);
      if (ok !== false) { setQuery(""); onClose(); }
    } finally {
      setBusy(null);
    }
  };

  return (
    <Modal open={open} onCancel={onClose} title="Copy a step" width={640} footer={null} rootClassName="dz" destroyOnHidden
           styles={{ body: { maxHeight: "65vh", overflowY: "auto", fontSize: 14 } }}>
      <div style={{ color: "#64748b", fontSize: 13, marginBottom: 10 }}>
        Steps of this process, by operation. The copy is added here with everything it carries — its role, signatures,
        rooms, equipment, fields, rules and parameter.
      </div>
      <Input allowClear placeholder="Find a step" value={query} onChange={(e) => setQuery(e.target.value)}
             style={{ marginBottom: 12 }} />
      {groups.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No steps to copy." />
      ) : (
        <Collapse size="small" defaultActiveKey={groups.map((g) => String(g.op.id))}
          items={groups.map(({ op, rows }) => ({
            key: String(op.id),
            label: (
              <span style={{ fontWeight: 600 }}>
                {op.name || op.code}
                {String(op.id) === String(current?.id) ? <Tag style={{ marginLeft: 8 }}>This operation</Tag> : null}
                <span style={{ fontWeight: 400, color: "#64748b", marginLeft: 6 }}>
                  · {rows.length} step{rows.length === 1 ? "" : "s"}
                </span>
              </span>
            ),
            children: rows.length ? rows.map(({ node, depth }) => {
              const blocked = why(node);
              return (
                <div key={node.code} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0",
                                              paddingLeft: depth * 18, borderBottom: "1px solid #f1f5f9" }}>
                  <code style={{ fontSize: 12.5, color: "#475569", minWidth: 64 }}>{node.code}</code>
                  <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={node.title || ""}>
                    {node.title || node.label || KIND_TEXT[node.kind] || "Untitled"}
                  </span>
                  <Tag style={{ marginInlineEnd: 0 }}>{typeLabel(node.step_type) || KIND_TEXT[node.kind] || node.kind}</Tag>
                  <Tooltip title={blocked || "Copy this step here"}>
                    <Button size="small" icon={<Copy size={14} />} disabled={!!blocked || (busy && busy !== node.code)}
                            loading={busy === node.code} onClick={() => copy(node)}>
                      Copy
                    </Button>
                  </Tooltip>
                </div>
              );
            }) : <div style={{ color: "#64748b", fontSize: 13 }}>No steps yet.</div>,
          }))} />
      )}
    </Modal>
  );
}
